/**
 * Determines how the user arrived at the current page for analytics tracking
 *
 * Priority order:
 * 1. UTM parameters (utm_source + utm_medium) - highest priority for marketing campaigns
 * 2. UTM source only (utm_source) - for basic campaign tracking
 * 3. External referrer domain - for organic traffic from other sites
 * 4. Internal referrer - for navigation within the same site
 * 5. Direct - for direct visits or when source cannot be determined
 *
 * @returns {string} Traffic source identifier
 * @example
 * // URL: https://example.com?utm_source=google&utm_medium=cpc
 * getTrafficSource() // Returns: "google/cpc"
 *
 * // URL: https://example.com?utm_source=facebook
 * getTrafficSource() // Returns: "facebook"
 *
 * // Referrer: https://google.com
 * getTrafficSource() // Returns: "google.com"
 *
 * // No referrer or UTM params
 * getTrafficSource() // Returns: "direct"
 */
export const getTrafficSource = () => {
  if (typeof window === 'undefined') return 'direct';

  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const referrer = document.referrer;

  if (utmSource && utmMedium) {
    return `${utmSource}/${utmMedium}`;
  } else if (utmSource) {
    return utmSource;
  } else if (referrer) {
    try {
      const referrerDomain = new URL(referrer).hostname;

      if (referrerDomain !== window.location.hostname) {
        return referrerDomain;
      }

      return 'internal';
    } catch {
      return 'direct';
    }
  }

  // Default: Direct visit (typed URL, bookmark, etc.)
  return 'direct';
};
